/**
 * Firebase Data Service
 * Firebase Realtime Database'e direkt erişim yerine Cloud Functions üzerinden veri çeker.
 * Bu sayede:
 * - Rate limiting uygulanır
 * - Yetki kontrolü merkezi yapılır
 * - Trafik izlenir ve kontrol edilir
 */

import { getFunctions, httpsCallable, HttpsCallable } from 'firebase/functions';
import app from '../firebase';
import ownAuthSession from './auth/ownAuthSession';
import { getBranches as getBranchesFromSql } from './migration/branchDataService';
import {
  getPersonnel as getPersonnelFromSql,
  getPersonnelByUid as getPersonnelByUidFromSql,
} from './migration/personnelDataService';
import { getUserByUid as getUserByUidFromSql } from './migration/userDataService';

// Firebase Functions instance (us-central1 region)
const functions = getFunctions(app, 'us-central1');

// Cloud Functions kullan/kullanma bayragi.
// USA modunda (`.env.usa` ile build) Cloud Functions deploy edilmedigi icin
// REACT_APP_USE_CLOUD_FUNCTIONS=false set edilir → tum callable'lar aninda reject
// olur ve cagiran taraftaki (AuthContext vb.) mevcut fallback path devreye girer.
// Default: enabled (TR uretim akisi bozulmaz).
const USE_CLOUD_FUNCTIONS = process.env.REACT_APP_USE_CLOUD_FUNCTIONS !== 'false';

if (!USE_CLOUD_FUNCTIONS) {
  console.warn('[FirebaseDataService] Cloud Functions DEVRE DISI (REACT_APP_USE_CLOUD_FUNCTIONS=false). Fallback kullaniliyor.');
}

// Response types
interface FirebaseDataResponse<T> {
  success: boolean;
  data: T | null;
  message?: string;
  timestamp?: number;
}

/**
 * Cloud Functions kullanilmiyorsa cagriyi aninda reject eden bir stub doner.
 * Boylece her metoddaki try/catch icindeki throw yakalanir ve cagiran taraf
 * (AuthContext vb.) zaten mevcut olan fallback path'ine duser.
 */
function wrapCallable<TIn, TOut>(name: string): HttpsCallable<TIn, TOut> {
  if (USE_CLOUD_FUNCTIONS) {
    const callable = httpsCallable<TIn, TOut>(functions, name);
    // ── GEÇİCİ TANI: Cloud Functions kaynak izleme (Faz 7.5) ──────────────────
    // CF sunucu tarafinda RTDB okur → dolayli RTDB. Her cagriyi [SOURCE:CF] ile loglar.
    if (process.env.NODE_ENV !== 'production') {
      return ((data?: TIn) => {
        // eslint-disable-next-line no-console
        console.log('[SOURCE:CF]', name);
        return callable(data as TIn);
      }) as unknown as HttpsCallable<TIn, TOut>;
    }
    return callable;
  }
  return ((_data?: TIn) => {
    return Promise.reject(new Error(`[FirebaseDataService] Cloud Functions disabled (${name}) - using fallback`));
  }) as unknown as HttpsCallable<TIn, TOut>;
}

// Callable function references with proper typing
// getUserData artik SQL (gts_auth_api) uzerinden — CF callable Faz A'da kaldirildi.
// getTasks/getWeeklyTasks/getMonthlyTasks/getTeamTasks/getSurveys CF callable'lari Faz E'de
// kaldirildi (gorevler taskDataService, anketler surveyDataService = SQL).
const getEventsCallable = wrapCallable<{ companyId: string; eventType?: string }, FirebaseDataResponse<any>>('getEvents');
// getAccounts/getCashbox/getCompanyData CF callable'lari Faz D/E'de kaldirildi
// (cari/kasa/sirket-verisi SQL; getCompanyData'nin tek tuketicisi olu ImageLibraryMigrationModal idi).

// User data type
export interface UserData {
  id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  companyId?: string;
  companyName?: string;
  role: string;
  branchesId?: string;
  employeeId?: string;
  photoURL?: string;
  createdAt?: number;
  kvkkAccepted?: boolean;
}

// Personnel data type
export interface PersonnelData {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  position?: string;
  department?: string;
  branchId?: string;
  userId?: string;
  isActive?: boolean;
  createdAt?: string;
}

// Branch data type
export interface BranchData {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  isHeadquarter?: boolean;
  isActive?: boolean;
  createdAt?: string;
}

// Task data types
export interface TaskData {
  id?: string;
  title?: string;
  description?: string;
  personnelId?: string;
  status?: string;
  priority?: string;
  dueDate?: string;
  createdAt?: string;
  completedAt?: string;
  [key: string]: any;
}

export interface TasksData {
  tasks?: { [key: string]: TaskData };
  completedTasks?: { [key: string]: TaskData };
  missedTasks?: { [key: string]: TaskData };
}

export interface WeeklyTasksData {
  weeklyTasks?: { [key: string]: TaskData };
  completedWeeklyTasks?: { [key: string]: TaskData };
  missedWeeklyTasks?: { [key: string]: TaskData };
}

export interface MonthlyTasksData {
  monthlyTasks?: { [key: string]: TaskData };
  completedMonthlyTasks?: { [key: string]: TaskData };
  missedMonthlyTasks?: { [key: string]: TaskData };
}

// Survey data type
export interface SurveyData {
  id?: string;
  title?: string;
  questions?: any[];
  responses?: { [key: string]: any };
  createdAt?: string;
  [key: string]: any;
}

// Event data type
export interface EventData {
  id?: string;
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  allDay?: boolean;
  [key: string]: any;
}

// Account data type
export interface AccountData {
  id?: string;
  name?: string;
  type?: string;
  balance?: number;
  [key: string]: any;
}

// Cashbox data type
export interface CashboxData {
  id?: string;
  name?: string;
  balance?: number;
  currency?: string;
  [key: string]: any;
}

// Cache için basit in-memory store
interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

const cache = new Map<string, CacheItem<any>>();
const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 dakika

function getCacheKey(prefix: string, ...args: string[]): string {
  return `${prefix}:${args.join(':')}`;
}

function getFromCache<T>(key: string): T | null {
  const item = cache.get(key);
  if (!item) return null;
  
  if (Date.now() > item.expiresAt) {
    cache.delete(key);
    return null;
  }
  
  return item.data as T;
}

function setToCache<T>(key: string, data: T, ttl: number = DEFAULT_CACHE_TTL): void {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    expiresAt: Date.now() + ttl
  });
}

/**
 * Firebase Data Service
 * Singleton pattern
 */
class FirebaseDataService {
  private static instance: FirebaseDataService;
  
  private constructor() {
    console.log('🔥 [FirebaseDataService] Initialized');
  }
  
  public static getInstance(): FirebaseDataService {
    if (!FirebaseDataService.instance) {
      FirebaseDataService.instance = new FirebaseDataService();
    }
    return FirebaseDataService.instance;
  }
  
  /**
   * Cache'i temizle
   */
  public clearCache(prefix?: string): void {
    if (prefix) {
      // Belirli prefix ile başlayan cache'leri temizle
      const keysToDelete: string[] = [];
      cache.forEach((_, key) => {
        if (key.startsWith(prefix)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => cache.delete(key));
      console.log(`🗑️ [FirebaseDataService] Cache cleared for prefix: ${prefix}`);
    } else {
      // Tüm cache'i temizle
      cache.clear();
      console.log('🗑️ [FirebaseDataService] All cache cleared');
    }
  }
  
  /**
   * Kullanıcı verisi getir
   * @param userId - Opsiyonel, belirtilmezse auth user'ın verisi gelir
   * @param useCache - Cache kullan (varsayılan: true)
   */
  public async getUserData(userId?: string, useCache: boolean = true): Promise<UserData | null> {
    // "self" -> oturum acmis kullanicinin uid'i (SQL ucu acik uid bekler).
    const uid = userId || ownAuthSession.getCurrentUid();
    if (!uid) {
      console.warn('⚠️ [FirebaseDataService] getUserData: uid yok (oturum acik degil?)');
      return null;
    }

    const cacheKey = getCacheKey('user', uid);

    if (useCache) {
      const cached = getFromCache<UserData>(cacheKey);
      if (cached) {
        console.log(`✅ [FirebaseDataService] getUserData from cache: ${uid}`);
        return cached;
      }
    }

    // SQL-only (gts_auth_api / SystemUsers). CF + RTDB fallback kaldirildi (Faz A).
    const data = await getUserByUidFromSql(uid);
    if (data) {
      setToCache(cacheKey, data);
      console.log(`✅ [FirebaseDataService] getUserData success (SQL): ${uid}`);
      return data;
    }

    console.warn(`⚠️ [FirebaseDataService] getUserData no data (SQL): ${uid}`);
    return null;
  }
  
  /**
   * Personel listesi getir
   * @param companyId - Şirket ID
   * @param personnelId - Opsiyonel, tek personel için
   * @param useCache - Cache kullan (varsayılan: true)
   */
  public async getPersonnel(
    companyId: string, 
    personnelId?: string,
    useCache: boolean = true
  ): Promise<PersonnelData | PersonnelData[] | null> {
    const cacheKey = getCacheKey('personnel', companyId, personnelId || 'all');
    
    if (useCache) {
      const cached = getFromCache<PersonnelData | PersonnelData[]>(cacheKey);
      if (cached) {
        console.log(`✅ [FirebaseDataService] getPersonnel from cache: ${companyId}/${personnelId || 'all'}`);
        return cached;
      }
    }
    
    try {
      // Faz 3: SQL-once + HybridFallback (personnelDataService TEK KAYNAK; id=uid korunur).
      // Cloud Functions yerine SQL → bu metodu kullanan vardiya/gorev/dashboard/rapor
      // taraflari otomatik SQL'e gecer. NOT: inline permission/department gibi RTDB'ye
      // ozgu alanlar SQL projeksiyonunda YOK; ilgili izin okumalari ayri ele alinir.
      if (personnelId) {
        const single = await getPersonnelByUidFromSql(personnelId, companyId);
        if (single) {
          const data = single as unknown as PersonnelData;
          setToCache(cacheKey, data);
          return data;
        }
        return null;
      }

      const list = await getPersonnelFromSql(companyId);
      const data = list as unknown as PersonnelData[];
      setToCache(cacheKey, data);
      console.log(`✅ [FirebaseDataService] getPersonnel → personnelDataService (SQL-once): ${companyId}, count: ${data.length}`);
      return data;

    } catch (error: any) {
      console.error('❌ [FirebaseDataService] getPersonnel error:', error);
      throw error;
    }
  }
  
  /**
   * Şube listesi getir
   * @param companyId - Şirket ID
   * @param useCache - Cache kullan (varsayılan: true)
   */
  public async getBranches(companyId: string, useCache: boolean = true): Promise<BranchData[]> {
    const cacheKey = getCacheKey('branches', companyId);
    
    if (useCache) {
      const cached = getFromCache<BranchData[]>(cacheKey);
      if (cached) {
        console.log(`✅ [FirebaseDataService] getBranches from cache: ${companyId}`);
        return cached;
      }
    }
    
    try {
      // Faz 2: SQL-once + HybridFallback Firebase okuma-yedegi (branchDataService).
      // Boylece bu metodu kullanan vardiya/gorev sistemleri otomatik SQL'e gecer.
      console.log(`📡 [FirebaseDataService] getBranches → branchDataService (SQL-once): ${companyId}`);
      const branches = await getBranchesFromSql(companyId);
      const data: BranchData[] = branches.map((b) => ({
        id: b.id,
        name: b.name,
        address: b.address,
        phone: b.phone,
        isHeadquarter: b.isHeadquarter,
        isActive: b.isActive,
      }));
      setToCache(cacheKey, data, 10 * 60 * 1000); // 10 dakika cache
      console.log(`✅ [FirebaseDataService] getBranches success: ${companyId}, count: ${data.length}`);
      return data;
    } catch (error: any) {
      console.error('❌ [FirebaseDataService] getBranches error:', error);
      throw error;
    }
  }
  
  /**
   * Etkinlikler getir
   * @param companyId - Şirket ID
   * @param eventType - 'calendarEvents' (varsayılan) veya diğer event tipleri
   * @param useCache - Cache kullan (varsayılan: true)
   */
  public async getEvents(
    companyId: string,
    eventType: string = 'calendarEvents',
    useCache: boolean = true
  ): Promise<any> {
    const cacheKey = getCacheKey('events', companyId, eventType);
    
    if (useCache) {
      const cached = getFromCache<any>(cacheKey);
      if (cached) {
        console.log(`✅ [FirebaseDataService] getEvents from cache: ${companyId}`);
        return cached;
      }
    }
    
    try {
      console.log(`📡 [FirebaseDataService] getEvents calling Cloud Function: ${companyId}/${eventType}`);
      const result = await getEventsCallable({ companyId, eventType });
      
      if (result.data.success && result.data.data) {
        setToCache(cacheKey, result.data.data, 5 * 60 * 1000); // 5 dakika cache
        console.log(`✅ [FirebaseDataService] getEvents success: ${companyId}`);
        return result.data.data;
      }
      
      console.warn(`⚠️ [FirebaseDataService] getEvents no data: ${result.data.message}`);
      return {};
      
    } catch (error: any) {
      console.error('❌ [FirebaseDataService] getEvents error:', error);
      throw error;
    }
  }
  
}

// Singleton export
const firebaseDataService = FirebaseDataService.getInstance();
export default firebaseDataService;

